import { Button, Card, InputNumber, Space, Typography, message } from 'antd';
import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import {
  DRIVER_FORCE_UPDATE_MIN_BUILD_KEY,
  PASSENGER_FORCE_UPDATE_MIN_BUILD_KEY,
} from '@taxima/shared';

async function loadMinBuild(key: string): Promise<number> {
  const { data } = await api.get<{ settingValue?: string }>(`/admin/settings/${key}`);
  const raw = data.settingValue?.trim() ?? '';
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

async function saveMinBuild(key: string, value: number) {
  await api.put(`/admin/settings/${key}`, { value: String(value) });
}

export default function AppForceUpdatePage() {
  const [passengerMin, setPassengerMin] = useState(0);
  const [driverMin, setDriverMin] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const [p, d] = await Promise.all([
          loadMinBuild(PASSENGER_FORCE_UPDATE_MIN_BUILD_KEY).catch(() => 0),
          loadMinBuild(DRIVER_FORCE_UPDATE_MIN_BUILD_KEY).catch(() => 0),
        ]);
        setPassengerMin(p);
        setDriverMin(d);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        saveMinBuild(PASSENGER_FORCE_UPDATE_MIN_BUILD_KEY, passengerMin),
        saveMinBuild(DRIVER_FORCE_UPDATE_MIN_BUILD_KEY, driverMin),
      ]);
      message.success('已儲存');
    } catch {
      message.error('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card loading={loading}>
      <Typography.Title level={4}>App 強制更新</Typography.Title>
      <Typography.Paragraph type="secondary">
        設定「最低 build number」：App 內建 build <strong>小於</strong> 此數字時，開 App
        會全屏強更提示，所有功能無法使用，直至用戶前往 App Store／Google Play（或你在「App
        分享連結」設的 Android 連結）更新。
        <br />
        設為 <strong>0</strong> 即關閉強更。目前正式版示例：1.0.0 (10) — build 填{' '}
        <strong>10</strong> 表示 build 9 及以下須更新，build 10 可正常使用。
      </Typography.Paragraph>

      <Space direction="vertical" size="large" style={{ width: '100%', maxWidth: 480 }}>
        <div>
          <Typography.Text strong>乘客 App — 最低 build number</Typography.Text>
          <InputNumber
            min={0}
            max={999999}
            precision={0}
            style={{ width: '100%', marginTop: 8 }}
            value={passengerMin}
            onChange={(v) => setPassengerMin(typeof v === 'number' ? v : 0)}
          />
        </div>
        <div>
          <Typography.Text strong>司機 App — 最低 build number</Typography.Text>
          <InputNumber
            min={0}
            max={999999}
            precision={0}
            style={{ width: '100%', marginTop: 8 }}
            value={driverMin}
            onChange={(v) => setDriverMin(typeof v === 'number' ? v : 0)}
          />
        </div>
        <Button type="primary" loading={saving} onClick={() => void save()}>
          儲存
        </Button>
      </Space>
    </Card>
  );
}
